import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { FiFilter, FiCheckCircle, FiClock, FiLock, FiSearch } from 'react-icons/fi';

const sampleRecords = [
  { id: '101', name: 'Custom Hook Patterns', module: 'React Architecture', status: 'Completed', date: '2023-11-20', xp: 250 },
  { id: '102', name: 'Zustand State Management', module: 'Global State', status: 'In Progress', date: '2023-11-22', xp: 120 },
  { id: '103', name: 'Framer Motion Physics', module: 'UI/UX Polish', status: 'Locked', date: '-', xp: 400 },
  { id: '104', name: 'Server Components', module: 'Next.js 14', status: 'In Progress', date: '2023-11-23', xp: 300 },
  { id: '105', name: 'WebAssembly Basics', module: 'Low-Level JS', status: 'Locked', date: '-', xp: 500 },
  { id: '106', name: 'TypeScript Utility Types', module: 'Core Logic', status: 'Completed', date: '2023-11-18', xp: 150 },
];

export default function DataExplorer() {
  const [filter, setFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredData = sampleRecords.filter(item => {
    const matchesFilter = filter === 'All' || item.status === filter;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="relative w-full md:w-64">
           <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
           <input 
             type="text" 
             placeholder="Search records..." 
             className="w-full bg-navy-900 border border-navy-700 rounded-lg py-2 pl-10 pr-4 text-sm focus:border-quest-500 outline-none transition-colors"
             value={searchTerm}
             onChange={(e) => setSearchTerm(e.target.value)}
           />
        </div>
        <div className="flex gap-2 p-1 bg-navy-900 border border-navy-700 rounded-xl overflow-x-auto w-full md:w-auto">
          {['All', 'Completed', 'In Progress', 'Locked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-xs font-mono transition-all whitespace-nowrap ${
                filter === f ? 'bg-navy-700 text-quest-500' : 'text-gray-500 hover:text-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-navy-900 border border-navy-700 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-navy-800/50 text-xs font-mono text-gray-500 uppercase">
                <th className="p-4 font-medium">Record ID</th>
                <th className="p-4 font-medium">Skill Node</th>
                <th className="p-4 font-medium">Module</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium text-right">XP Yield</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-navy-800">
              {filteredData.map((item, idx) => (
                <motion.tr 
                  key={item.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="hover:bg-navy-800/30 transition-colors group"
                >
                  <td className="p-4 text-xs font-mono text-gray-500">#{item.id}</td>
                  <td className="p-4 font-bold text-white">{item.name}</td>
                  <td className="p-4 text-sm text-gray-400">{item.module}</td>
                  <td className="p-4">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${
                      item.status === 'Completed' ? 'bg-quest-500/10 text-quest-500 border-quest-500/30' :
                      item.status === 'In Progress' ? 'bg-neon-blue/10 text-neon-blue border-neon-blue/30' :
                      'bg-navy-800 text-gray-500 border-navy-700'
                    }`}>
                      {item.status === 'Completed' && <SafeIcon icon={FiCheckCircle} />}
                      {item.status === 'In Progress' && <SafeIcon icon={FiClock} className="animate-pulse" />}
                      {item.status === 'Locked' && <SafeIcon icon={FiLock} />}
                      {item.status}
                    </span>
                  </td>
                  <td className="p-4 text-right font-mono text-trophy-500">+{item.xp}</td>
                </motion.tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-gray-500 font-mono italic">
                    No records found matching critera.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}